/**
 * Types for the outbound programs feature (migration 20261010300000).
 * Mirrors the tables and RPC payloads; see docs/outreach-programs.md.
 */

export type ProgramKind = 'editorial' | 'sales' | 'other'

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type DayKey = (typeof DAY_KEYS)[number]

export interface RampRow {
  /** ISO date (yyyy-mm-dd) in the business timezone from which `cap` applies. */
  from: string
  cap: number
}

export interface Program {
  id: string
  workspace_id: string
  site_id: string | null
  name: string
  kind: ProgramKind
  from_label: string | null
  daily_cap: number
  ramp: RampRow[]
  cooldown_days: number
  never_same_address: boolean
  bounce_stop_pct: number
  /** The bounce stop only applies once the 7 day window has at least this many sends. */
  bounce_min_sample: number
  send_days: DayKey[]
  blocked_phrases: string[]
  active: boolean
  sequence_id: string | null
  created_at: string
  updated_at: string
}

/** Writable subset of a program. */
export type ProgramInput = Omit<Program, 'id' | 'workspace_id' | 'created_at' | 'updated_at'>

export interface Sequence {
  id: string
  workspace_id: string
  name: string
  final_stage: string | null
}

export interface SequenceStep {
  step_no: number
  day_offset: number
  subject: string
  body: string
  same_thread: boolean
}

export type SuppressionKind = 'email' | 'domain' | 'company'

export interface Suppression {
  id: string
  kind: SuppressionKind
  value: string
  reason: string | null
  source: string | null
  created_at: string
}

export interface SuppressionInput {
  kind: SuppressionKind
  value: string
  reason?: string
  source?: string
}

export type CustomFieldEntity = 'contact' | 'account'
export type CustomFieldType = 'text' | 'select' | 'multi_select' | 'url' | 'number' | 'date'

export interface CustomFieldDef {
  id: string
  entity: CustomFieldEntity
  key: string
  label: string
  type: CustomFieldType
  options: string[]
  position: number
}

export type CustomFieldValue = string | number | string[] | null
export type CustomFieldValues = Record<string, CustomFieldValue>

/** Paused reasons returned by program_status / program_queue. */
export type PausedReason =
  | 'inactive'
  | 'no_sequence'
  | 'not_a_send_day'
  | 'bounce_stop'
  | 'cap_reached'

export interface ProgramStatus {
  program_id: string
  workspace_id: string
  timezone: string
  local_date: string
  local_day: DayKey
  active: boolean
  cap_today: number
  sent_today: number
  claims_pending: number
  remaining: number
  sent_7d: number
  bounces_7d: number
  bounce_rate_7d: number
  paused_reason: PausedReason | null
}

export interface ProgramStatRow {
  program_id: string
  name: string
  kind: ProgramKind
  active: boolean
  site_id: string | null
  from_label: string | null
  cap_today: number
  sent_today: number
  remaining: number
  paused_reason: PausedReason | null
  sent_7d: number
  sent_30d: number
  replies: number
  bounces: number
  optouts: number
  queued: number
  due_today: number
}

export interface StatsTotals {
  sent_today: number
  sent_7d: number
  sent_30d: number
  /** Sends inside the selected window (default: last 30 days). */
  sent: number
  replies: number
  bounces: number
  optouts: number
  reply_rate: number | null
  bounce_rate: number | null
  queued: number
  due_today: number
  suppressions: number
}

export interface SiteStatRow {
  site_id: string | null
  name: string
  sent: number
  replies: number
  bounces: number
  optouts: number
}

export interface MailboxStatRow {
  mailbox: string
  sent: number
  replies: number
  bounces: number
}

export interface StageCount {
  stage: string
  count: number
}

export interface DealStageRow {
  stage: string
  count: number
  value: number
}

export interface WorkspaceStats {
  workspace_id: string
  timezone: string
  window: { from: string; to: string }
  totals: StatsTotals
  by_program: ProgramStatRow[]
  by_site: SiteStatRow[]
  by_mailbox: MailboxStatRow[]
  pipeline: Partial<Record<ProgramKind, StageCount[]>>
  deals_by_stage: DealStageRow[]
}

export interface OverviewBusinessStats {
  workspace_id: string
  name: string
  kind: 'personal' | 'business'
  role: 'owner' | 'admin' | 'member'
  stats: WorkspaceStats
}

export interface OutreachOverview {
  totals: {
    businesses: number
    sent_today: number
    sent_7d: number
    sent_30d: number
    sent: number
    replies: number
    bounces: number
    optouts: number
    queued: number
    due_today: number
    reply_rate: number | null
    bounce_rate: number | null
  }
  businesses: OverviewBusinessStats[]
}

export interface EnrollResult {
  requested: number
  enrolled: number
  already_enrolled: number
  no_email: number
  not_found: number
}

export interface SiteRef {
  id: string
  name: string
  domain: string
}
